/**
 * Simple Image Tool for EditorJS
 * Displays images without upload functionality - read-only display even in edit mode
 */

import { signImageUrl } from './utils';

export class SimpleImageTool {
  private api: any;
  private readOnly: boolean;
  private data: {
    file?: { url: string };
    url?: string;
    caption?: string;
  };
  private wrapper: HTMLElement | null = null;
  private imageElement: HTMLImageElement | null = null;

  static get toolbox() {
    return {
      title: 'Image',
      icon: '<svg width="17" height="15" viewBox="0 0 336 276" xmlns="http://www.w3.org/2000/svg"><path d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 56-29 42 30zm0 52l-43-30-56 30-81-67-66 39v23c0 19 15 34 34 34h178c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, api, readOnly }: { data: any; api: any; readOnly: boolean }) {
    this.api = api;
    this.readOnly = readOnly;
    this.data = {
      file: data.file || undefined,
      url: data.url || undefined,
      caption: data.caption || ''
    };
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('simple-image-tool');

    const imageUrl = this.data.file?.url || this.data.url;

    if (imageUrl) {
      const imageContainer = document.createElement('div');
      imageContainer.classList.add('simple-image-tool__image-container');
      
      this.imageElement = document.createElement('img');
      this.imageElement.classList.add('simple-image-tool__image');
      
      // Make image responsive
      this.imageElement.style.maxWidth = '100%';
      this.imageElement.style.height = 'auto';
      this.imageElement.style.display = 'block';
      this.imageElement.style.margin = '0 auto';
      
      if (imageUrl.startsWith('/outputs/extracted_images/')) {
        // Use transparent placeholder while signing — avoids 403 from nginx
        this.imageElement.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        signImageUrl(imageUrl).then(signedUrl => {
          if (this.imageElement) {
            this.imageElement.src = signedUrl;
          }
        }).catch(() => {
          // Signing failed; leave the placeholder
        });
      } else {
        this.imageElement.src = imageUrl;
      }
      
      imageContainer.appendChild(this.imageElement);
      this.wrapper.appendChild(imageContainer);

      if (this.data.caption) {
        const caption = document.createElement('div');
        caption.classList.add('simple-image-tool__caption');
        caption.textContent = this.data.caption;
        caption.style.textAlign = 'center';
        caption.style.fontSize = '0.875rem';
        caption.style.color = '#6b7280';
        caption.style.marginTop = '0.5rem';
        this.wrapper.appendChild(caption);
      }
    } else {
      // No image URL provided
      const placeholder = document.createElement('div');
      placeholder.textContent = 'Image (no URL provided)';
      placeholder.style.padding = '1rem';
      placeholder.style.backgroundColor = '#f3f4f6';
      placeholder.style.borderRadius = '0.375rem';
      placeholder.style.textAlign = 'center';
      placeholder.style.color = '#9ca3af';
      this.wrapper.appendChild(placeholder);
    }

    return this.wrapper;
  }

  save() {
    return this.data;
  }

  validate(savedData: any) {
    // Image is valid if it has a URL
    return !!(savedData.file?.url || savedData.url);
  }

  static get sanitize() {
    return {
      url: {},
      caption: {},
      file: {
        url: {}
      }
    };
  }
}

